import { apiClient } from './client';
import type { Doctor, PaginatedResponse } from '@/types/models';

// Re-export types for convenience
export type { Doctor };

export interface CreateDoctorData {
  name: string;
  email?: string;
  crm?: string;
  specialty?: string;
  phone?: string;
  isActive?: boolean;
}

export interface UpdateDoctorData extends Partial<CreateDoctorData> {}

export interface DoctorsQueryParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface DoctorWithClinics extends Doctor {
  clinicIds?: string[];
}

export const doctorsService = {
  // Get all doctors with pagination
  async getAll(params?: DoctorsQueryParams): Promise<PaginatedResponse<Doctor>> {
    const response = await apiClient.get('/doctors', { params });
    return response.data;
  },

  // Get accessible doctors for current user
  async getAccessible(): Promise<Doctor[]> {
    const response = await apiClient.get('/doctors/accessible');
    return response.data;
  },

  // Search doctors
  async search(query: string, fields?: string[]): Promise<Doctor[]> {
    const response = await apiClient.get('/doctors/search', {
      params: { q: query, fields: fields?.join(',') },
    });
    return response.data;
  },

  // Get single doctor by ID
  async getById(id: string): Promise<DoctorWithClinics> {
    const response = await apiClient.get(`/doctors/${id}`);
    return response.data;
  },

  // Create new doctor
  async create(data: CreateDoctorData): Promise<Doctor> {
    const response = await apiClient.post('/doctors', data);
    return response.data;
  },

  // Update doctor
  async update(id: string, data: UpdateDoctorData): Promise<Doctor> {
    const response = await apiClient.put(`/doctors/${id}`, data);
    return response.data;
  },

  // Toggle doctor status
  async toggleStatus(id: string): Promise<Doctor> {
    const response = await apiClient.patch(`/doctors/${id}/status`);
    return response.data;
  },

  // Delete doctor
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/doctors/${id}`);
  },

  // Get clinics for a doctor
  async getClinics(doctorId: string): Promise<any[]> {
    const response = await apiClient.get(`/doctors/${doctorId}/clinics`);
    return response.data;
  },

  // Add doctor to clinic
  async addToClinic(doctorId: string, clinicId: string): Promise<void> {
    await apiClient.post(`/doctors/${doctorId}/clinics/${clinicId}`);
  },

  // Remove doctor from clinic
  async removeFromClinic(doctorId: string, clinicId: string): Promise<void> {
    await apiClient.delete(`/doctors/${doctorId}/clinics/${clinicId}`);
  },

  // Get patients for a doctor
  async getPatients(doctorId: string): Promise<any> {
    const response = await apiClient.get(`/doctors/${doctorId}/patients`);
    return response.data;
  },
};